import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../components/Cart';
import AOS from 'aos';
import 'aos/dist/aos.css';
import {
  Pane,
  Heading,
  TextInput,
  Button,
  toaster,
  majorScale,
  Text,
  Select,
  FormField,
  Table,
  IconButton,
  EyeOpenIcon,
} from 'evergreen-ui';
import { jwtDecode } from 'jwt-decode';

// Helper function to calculate cart total
const calculateCartTotal = (cartItems) => {
  return cartItems.reduce((acc, item) => acc + (item.price * (item.quantity || 0)), 0);
};

const Checkout = () => {
  const { cartItems, clearCart } = useCart();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [countyName, setCountyName] = useState('');
  const [pickupPoint, setPickupPoint] = useState('');
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);

  // Initialize form data from localStorage
  useEffect(() => {
    try {
      const savedCheckout = localStorage.getItem('checkoutData');
      if (savedCheckout) {
        const { data, expiry } = JSON.parse(savedCheckout);
        if (expiry && Date.now() < expiry) {
          setFirstName(data.firstName || '');
          setLastName(data.lastName || '');
          setEmail(data.email || '');
          setPhone(data.phone || '');
          setCountyName(data.countyName || '');
          setPickupPoint(data.pickupPoint || '');
        }
      }
    } catch (error) {
      console.error('Error reading checkout data from localStorage:', error);
    }
  }, []);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    try {
      if (firstName || lastName || email || phone || countyName || pickupPoint) {
        const checkoutData = {
          data: {
            firstName,
            lastName,
            email,
            phone,
            countyName,
            pickupPoint,
          },
          expiry: Date.now() + 7 * 24 * 60 * 60 * 1000, // 1 week in milliseconds
        };
        localStorage.setItem('checkoutData', JSON.stringify(checkoutData));
      }
    } catch (error) {
      console.error('Error saving checkout data to localStorage:', error);
    }
  }, [firstName, lastName, email, phone, countyName, pickupPoint]);

  // Initialize AOS and scroll to top
  useEffect(() => {
    window.scrollTo(0, 0); // Fixed: Corrected from '0-toolbar' to '0'
    AOS.init();
    AOS.refresh();
  }, []);

  // Redirect to cart if cartItems is empty
  useEffect(() => {
    if (cartItems.length === 0) {
      toaster.warning('Your cart is empty. Please add items to proceed.');
      navigate('/cart');
    }
  }, [cartItems, navigate]);

  // Check authentication and redirect to login if not authenticated
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      toaster.danger('Please log in to proceed to checkout');
      navigate('/login');
      return;
    }
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decoded.exp < currentTime) {
        toaster.danger('Your session has expired. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      }
    } catch (error) {
      toaster.danger('Invalid token. Please log in again.');
      localStorage.removeItem('token');
      navigate('/login');
    }
  }, [navigate]);

  // Fetch locations from the backend
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await fetch('http://localhost:5000/locations');
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch locations');
        }
        const data = await response.json();
        setLocations(data);
      } catch (error) {
        toaster.danger('Error fetching locations: ' + error.message);
      }
    };
    fetchLocations();
  }, []);

  // Fetch saved addresses from the backend
  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const decoded = jwtDecode(token);
        const userId = decoded.id;

        const response = await fetch(`http://localhost:5000/shipping-address/user/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (!response.ok) throw new Error('Failed to fetch addresses');
        const data = await response.json();
        setAddresses(data.data);
      } catch (error) {
        toaster.danger('Error fetching addresses: ' + error.message);
      }
    };
    fetchAddresses();
  }, []);

  // Calculate totals
  const cartTotal = calculateCartTotal(cartItems);
  const selectedLocation = locations.find((loc) => loc.pickupPoint === pickupPoint);
  const deliveryFee = selectedLocation ? selectedLocation.deliveryFee : 0;
  const grandTotal = cartTotal + deliveryFee;

  // Group locations by countyName for dropdown
  const groupedLocations = locations.reduce((acc, location) => {
    if (!acc[location.countyName]) {
      acc[location.countyName] = [];
    }
    acc[location.countyName].push(location);
    return acc;
  }, {});

  // Handle clicking an address suggestion
  const handleSelectAddress = (address) => {
    setFirstName(address.firstName);
    setLastName(address.lastName);
    setEmail(address.email);
    setPhone(address.phoneNumber);
    setCountyName(address.countyName);
    setPickupPoint(address.pickupPoint);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const token = localStorage.getItem('token');
    let userId;
    try {
      const decoded = jwtDecode(token);
      userId = decoded.id;
    } catch (error) {
      toaster.danger('Invalid token. Please log in again.');
      navigate('/login');
      setIsLoading(false);
      return;
    }

    // Validate cartItems before sending
    const validatedCartItems = cartItems.map((item) => ({
      productId: item.id || item.productId,
      quantity: item.quantity || 0,
      price: item.price,
    }));

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/checkout-pesapal/order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          cartItems: validatedCartItems,
          pickupPoint,
          email,
          firstName,
          lastName,
          phone,
          amount: grandTotal,
          userId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to initiate payment');
      }

      if (data.redirectUrl) {
        clearCart();
        window.location.assign(data.redirectUrl);
      } else {
        throw new Error('No redirect URL received');
      }
    } catch (error) {
      toaster.danger(error.message, {
        description: error.details ? JSON.stringify(error.details) : undefined,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Pane
      display="flex"
      flexDirection="column"
      alignItems="center"
      minHeight="100vh"
      background="tint2"
      padding={majorScale(3)}
    >
      <Heading size={900} marginBottom={majorScale(3)}>
        Checkout
      </Heading>
      <Pane
        width="100%"
        maxWidth={600}
        background="white"
        padding={majorScale(3)}
        borderRadius={8}
        elevation={1}
      >
        {/* Saved Addresses Suggestions */}
        {addresses.length > 0 && (
          <Pane marginBottom={majorScale(3)}>
            <Heading size={500} marginBottom={majorScale(2)}>
              Saved Addresses
            </Heading>
            <Table>
              <Table.Head>
                <Table.TextHeaderCell>Name</Table.TextHeaderCell>
                <Table.TextHeaderCell>Details</Table.TextHeaderCell>
                <Table.TextHeaderCell>Action</Table.TextHeaderCell>
              </Table.Head>
              <Table.Body>
                {addresses.map((address) => (
                  <Table.Row key={address.id}>
                    <Table.TextCell>
                      {address.firstName} {address.lastName}
                    </Table.TextCell>
                    <Table.TextCell>
                      {address.pickupPoint}, {address.countyName}
                    </Table.TextCell>
                    <Table.Cell>
                      <IconButton
                        icon={EyeOpenIcon}
                        onClick={() => handleSelectAddress(address)}
                        title="Use this address"
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Pane>
        )}

        {/* Divider */}
        {addresses.length > 0 && (
          <Pane
            borderTop="1px solid #E6E8F0"
            marginY={majorScale(3)}
          />
        )}

        <form onSubmit={handleSubmit}>
          <Pane marginBottom={majorScale(2)}>
            <span style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>
              First Name
            </span>
            <TextInput
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="e.g., John"
              width="100%"
              required
            />
          </Pane>
          <Pane marginBottom={majorScale(2)}>
            <span style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>
              Last Name
            </span>
            <TextInput
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="e.g., Doe"
              width="100%"
              required
            />
          </Pane>
          <Pane marginBottom={majorScale(2)}>
            <span style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>
              Email
            </span>
            <TextInput
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g., john.doe@example.com"
              width="100%"
              required
            />
          </Pane>
          <Pane marginBottom={majorScale(2)}>
            <span style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}>
              Phone Number
            </span>
            <TextInput
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g., +254123456789"
              width="100%"
              required
            />
          </Pane>
          <Pane marginBottom={majorScale(2)}>
            <FormField label="Pickup Location">
              <Select
                value={countyName}
                onChange={(e) => {
                  setCountyName(e.target.value);
                  setPickupPoint('');
                }}
                disabled={!locations.length}
                width="100%"
                marginBottom={8}
              >
                <option value="">Select County</option>
                {Object.keys(groupedLocations).map((county) => (
                  <option key={county} value={county}>
                    {county}
                  </option>
                ))}
              </Select>
              {countyName && (
                <Select
                  value={pickupPoint}
                  onChange={(e) => setPickupPoint(e.target.value)}
                  disabled={!countyName}
                  width="100%"
                >
                  <option value="">Select Pickup Point</option>
                  {groupedLocations[countyName]?.map((location) => (
                    <option key={location.id} value={location.pickupPoint}>
                      {location.pickupPoint} (USD {location.deliveryFee.toFixed(2)})
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
          </Pane>
          <Pane marginTop={majorScale(2)}>
            <Text size={500} display="block" marginBottom={majorScale(1)}>
              Subtotal: USD {cartTotal.toFixed(2)}
            </Text>
            <Text size={500} display="block" marginBottom={majorScale(1)}>
              Delivery Fee: USD {deliveryFee.toFixed(2)}
            </Text>
            <Text
              size={600}
              fontWeight="bold"
              display="block"
              marginBottom={majorScale(1)}
            >
              Total (Including VAT): USD {grandTotal.toFixed(2)}
            </Text>
          </Pane>
          <Pane marginTop={majorScale(2)}>
            <Button
              appearance="primary"
              type="submit"
              isLoading={isLoading}
              disabled={
                !firstName ||
                !lastName ||
                !email ||
                !phone ||
                !countyName ||
                !pickupPoint ||
                !cartItems.length
              }
            >
              Proceed to Pay
            </Button>
          </Pane>
        </form>
      </Pane>
    </Pane>
  );
};

export default Checkout;